/* ══════════════════════════════════════════════════════════════
   News Ticker — Top Priority Headlines with Badges
   Shows top 5 scored news items on Dashboard
   ══════════════════════════════════════════════════════════════ */

import { fetchLLMPriorityFeed } from '../services/llm-api';

export interface TickerItem {
  title: string;
  score: number;
  category: string;
  impact: string;
  source: string;
  timeAgo: string;
  whyItMatters: string;
}

const CATEGORY_COLORS: Record<string, string> = {
  policy: '#ef4444', earnings: '#22c55e', markets: '#3b82f6',
  ipo: '#a855f7', commodities: '#f59e0b', banking: '#0ea5e9',
  tech: '#8b5cf6', global: '#6366f1', corporate: '#ec4899',
  auto: '#14b8a6', pharma: '#06b6d4', energy: '#f97316',
};

const IMPACT_EMOJI: Record<string, string> = {
  critical: '🔴', high: '🟠', medium: '🟡', low: '🟢',
};

function getDefaultItems(): TickerItem[] {
  return [
    { title: 'RBI holds repo rate at 6.5%, signals dovish tilt', score: 92, category: 'policy', impact: 'critical', source: 'Reuters', timeAgo: '35m', whyItMatters: 'April rate cut now 75% probable — bullish for banks' },
    { title: 'HDFC Bank Q3 PAT jumps 18% to ₹16,800 crore', score: 88, category: 'earnings', impact: 'high', source: 'Moneycontrol', timeAgo: '2h', whyItMatters: 'Beat consensus by 3%, strong NIM expansion' },
    { title: 'FIIs buy ₹2,300 crore — 5th consecutive day', score: 85, category: 'markets', impact: 'high', source: 'ET', timeAgo: '4h', whyItMatters: 'Strongest inflow streak since Nov 2024' },
    { title: 'Crude drops below $78 on OPEC+ output signal', score: 78, category: 'commodities', impact: 'high', source: 'LiveMint', timeAgo: '6h', whyItMatters: 'Lower import bill supports INR' },
    { title: 'Govt approves ₹75,000 Cr semiconductor scheme', score: 70, category: 'policy', impact: 'medium', source: 'FE', timeAgo: '10h', whyItMatters: 'India semi push — watch Tata Electronics' },
  ];
}

function renderWithData(container: HTMLElement, data: TickerItem[]): void {
  const tickerItems = data.map((item, i) => `
    <div class="ticker-item" style="animation-delay: ${i * 0.05}s">
      <div class="ticker-item-header">
        <span class="ticker-score">${item.score}</span>
        <span class="ticker-impact">${IMPACT_EMOJI[item.impact] || '🟡'}</span>
        <span class="ticker-category" style="background: ${CATEGORY_COLORS[item.category] || '#666'}20; color: ${CATEGORY_COLORS[item.category] || '#666'}">${item.category}</span>
        <span class="ticker-title">${item.title}</span>
      </div>
      <div class="ticker-item-footer">
        <span class="ticker-why">💡 ${item.whyItMatters}</span>
        <span class="ticker-meta">${item.source} · ${item.timeAgo}</span>
      </div>
    </div>`).join('');

  container.innerHTML = `
    <div class="glass-card news-ticker-panel">
      <div class="card-header">
        <h3>📰 Priority News</h3>
        <a href="#/intelligence" class="view-all">View All →</a>
      </div>
      <div class="ticker-list">
        ${tickerItems}
      </div>
    </div>`;
}

export function renderNewsTicker(container: HTMLElement, items?: TickerItem[]): void {
  if (items) {
    renderWithData(container, items);
  } else {
    container.innerHTML = `<div class="glass-card news-ticker-panel" style="padding:2rem;text-align:center;">Loading priority news...</div>`;
    fetchLLMPriorityFeed().then(feed => {
      if (feed && feed.length > 0) {
        const mapped: TickerItem[] = feed.slice(0, 5).map((item: Record<string, unknown>) => ({
          title: String(item.title || ''),
          score: Number(item.score || 50),
          category: String(item.category || 'markets'),
          impact: String(item.impact || 'medium'),
          source: String(item.source || ''),
          timeAgo: String(item.timeAgo || item.time_ago || ''),
          whyItMatters: String(item.whyItMatters || item.why_it_matters || item.explanation || ''),
        }));
        renderWithData(container, mapped);
      } else {
        renderWithData(container, getDefaultItems());
      }
    }).catch(() => {
      renderWithData(container, getDefaultItems());
    });
  }
}
